import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  message,
  Typography,
  DatePicker,
  TimePicker,
  Spin
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faTimes,
  faUser,
  faBuilding,
  faMapMarkerAlt,
  faCog,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import BookingService from '../services/BookingService';
import EventTypeService from '../services/EventTypeService';
import dayjs from 'dayjs';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EditBookingModal = ({ 
  visible, 
  booking, 
  onCancel, 
  onSuccess 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);

  // Load event types when modal opens
  useEffect(() => {
    if (visible) {
      loadEventTypes();
    }
  }, [visible]);

  const loadEventTypes = async () => {
    setLoadingEventTypes(true);
    try {
      const response = await EventTypeService.getEventTypes();
      if (response.success) {
        setEventTypes(response.data.eventTypes || []);
      }
    } catch (error) {
      console.error('Error loading event types:', error);
    } finally {
      setLoadingEventTypes(false);
    }
  };

  useEffect(() => {
    if (booking && visible) {
      // Initialize form with booking data
      // Note: booking.eventType is the populated object, booking.eventTypeId may be just the ID
      form.setFieldsValue({
        firstName: booking.userInfo?.firstName || '',
        lastName: booking.userInfo?.lastName || '',
        email: booking.userInfo?.email || '',
        phone: booking.userInfo?.phone || '',
        eventTypeId: booking.eventType?._id || booking.eventTypeId?._id || booking.eventTypeId || '',
        status: booking.status || 'confirmed',
        notes: booking.notes || '',
        // Appointment date and time
        bookingDate: booking.bookingDate ? dayjs(booking.bookingDate) : null,
        endDate: booking.endDate ? dayjs(booking.endDate) : null,
        startTime: booking.timeSlot?.startTime ? dayjs(booking.timeSlot.startTime) : null,
        endTime: booking.timeSlot?.endTime ? dayjs(booking.timeSlot.endTime) : null,
        // Custom data fields - check both eventLocation and customData for backward compatibility
        businessEventName: booking.eventLocation?.eventName || booking.userInfo?.customData?.businessEventName || '',
        pointOfContactName: booking.userInfo?.customData?.pointOfContactName || '',
        pointOfContactInfo: booking.userInfo?.customData?.pointOfContactInfo || '',
        address1: booking.eventLocation?.address || booking.userInfo?.customData?.address1 || '',
        city: booking.eventLocation?.city || booking.userInfo?.customData?.city || '',
        state: booking.eventLocation?.state || booking.userInfo?.customData?.state || '',
        zipCode: booking.eventLocation?.zipCode || booking.userInfo?.customData?.zipCode || '',
        eventStartTime: booking.userInfo?.customData?.eventStartTime || '',
        screeningStartTime: booking.userInfo?.customData?.screeningStartTime || '',
        indoorOutdoorEvent: booking.userInfo?.customData?.indoorOutdoorEvent || '',
        anticipatedParticipants: booking.userInfo?.customData?.anticipatedParticipants || '',
        volunteersAvailable: booking.userInfo?.customData?.volunteersAvailable || ''
      });
    }
  }, [booking, visible, form]);

  const handleSubmit = async (values) => {
    if (!booking) return;

    setLoading(true);
    try {
      // Prepare update data
      const updateData = {
        userInfo: {
          ...booking.userInfo,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          customData: {
            ...booking.userInfo?.customData,
            businessEventName: values.businessEventName,
            pointOfContactName: values.pointOfContactName,
            pointOfContactInfo: values.pointOfContactInfo,
            address1: values.address1,
            city: values.city,
            state: values.state,
            zipCode: values.zipCode,
            eventStartTime: values.eventStartTime,
            screeningStartTime: values.screeningStartTime,
            indoorOutdoorEvent: values.indoorOutdoorEvent,
            anticipatedParticipants: values.anticipatedParticipants,
            volunteersAvailable: values.volunteersAvailable
          }
        },
        eventLocation: {
          eventName: values.businessEventName,
          address: values.address1,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode
        },
        // Appointment date and time
        bookingDate: values.bookingDate ? values.bookingDate.toISOString() : booking.bookingDate,
        endDate: values.endDate ? values.endDate.toISOString() : null,
        timeSlot: {
          ...booking.timeSlot, // preserve existing fields like timeSlot.date
          startTime: values.startTime ? values.startTime.toISOString() : booking.timeSlot?.startTime,
          endTime: values.endTime ? values.endTime.toISOString() : booking.timeSlot?.endTime
        },
        eventTypeId: values.eventTypeId,
        status: values.status,
        notes: values.notes
      };

      const response = await BookingService.updateBookingDetails(booking._id, updateData);

      if (response.success) {
        // Check for conflict warnings
        if (response.data?.warnings && response.data.warnings.length > 0) {
          // Show warning notification for time conflicts
          response.data.warnings.forEach(warning => {
            message.warning(`⚠️ Time conflict: ${warning.message}`, 5);
          });
          message.success('Booking updated (with time conflicts)', 3);
        } else {
          message.success('Booking updated successfully');
        }
        onSuccess?.(response.data?.data || response.data);
        onCancel();
      } else {
        message.error(response.error || 'Failed to update booking');
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      message.error('Failed to update booking');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  if (!booking) return null;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FontAwesomeIcon icon={faUser} style={{ color: '#1890ff' }} />
          <span style={{ fontSize: '18px', fontWeight: '600' }}>Edit Booking Details</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={900}
      footer={null}
      styles={{
        body: { padding: '0' }
      }}
    >
      <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ padding: '24px' }}
        >
          {/* Client Information Section */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '12px' 
          }}>
            <FontAwesomeIcon icon={faUser} style={{ color: '#1890ff', fontSize: '16px' }} />
            <Title level={4} style={{ margin: 0, color: '#1a1a1a' }}>
              Client Information
            </Title>
          </div>
          <div style={{ 
            marginBottom: '32px',
            padding: '24px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0'
          }}>
            
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[{ required: true, message: 'Please enter first name' }]}
                >
                  <Input 
                    placeholder="First name" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please enter last name' }]}
                >
                  <Input 
                    placeholder="Last name" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Please enter email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input 
                    placeholder="Email address" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[{ required: true, message: 'Please enter phone number' }]}
                >
                  <Input
                    placeholder="Phone number"
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Appointment Date & Time Section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#fa8c16', fontSize: '16px' }} />
            <Title level={4} style={{ margin: 0, color: '#1a1a1a' }}>
              Appointment Date & Time
            </Title>
          </div>
          <div style={{
            marginBottom: '32px',
            padding: '24px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0'
          }}>

            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item
                  name="bookingDate"
                  label="Start Date"
                  rules={[{ required: true, message: 'Please select date' }]}
                >
                  <DatePicker
                    size="large"
                    style={{ width: '100%', height: '48px' }}
                    format="MMM D, YYYY"
                    placeholder="Select date"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="endDate"
                  label="End Date (Multi-Day)"
                  extra={<span style={{ fontSize: '11px', color: '#999' }}>Leave empty for single-day</span>}
                >
                  <DatePicker
                    size="large"
                    style={{ width: '100%', height: '48px' }}
                    format="MMM D, YYYY"
                    placeholder="End date (optional)"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="startTime"
                  label="Start Time"
                  rules={[{ required: true, message: 'Please select start time' }]}
                >
                  <TimePicker
                    size="large"
                    style={{ width: '100%', height: '48px' }}
                    format="h:mm A"
                    use12Hours
                    placeholder="Start time"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="endTime"
                  label="End Time"
                  rules={[{ required: true, message: 'Please select end time' }]}
                >
                  <TimePicker
                    size="large"
                    style={{ width: '100%', height: '48px' }}
                    format="h:mm A"
                    use12Hours
                    placeholder="End time"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Booking Status & Notes */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <FontAwesomeIcon icon={faCog} style={{ color: '#52c41a', fontSize: '16px' }} />
            <Title level={4} style={{ margin: 0, color: '#1a1a1a' }}>
              Booking Status & Notes
            </Title>
          </div>
          <div style={{
            marginBottom: '32px',
            padding: '24px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0'
          }}>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  name="eventTypeId"
                  label="Event Type"
                  rules={[{ required: true, message: 'Please select event type' }]}
                >
                  <Select
                    placeholder="Select event type"
                    size="large"
                    style={{ height: '48px' }}
                    loading={loadingEventTypes}
                    notFoundContent={loadingEventTypes ? <Spin size="small" /> : 'No event types found'}
                  >
                    {eventTypes.map(et => (
                      <Option key={et._id} value={et._id}>
                        {et.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="status"
                  label="Booking Status"
                  rules={[{ required: true, message: 'Please select status' }]}
                >
                  <Select
                    placeholder="Select status"
                    size="large"
                    style={{ height: '48px' }}
                  >
                    <Option value="pending">Pending</Option>
                    <Option value="confirmed">Confirmed</Option>
                    <Option value="completed">Completed</Option>
                    <Option value="cancelled">Cancelled</Option>
                    <Option value="no-show">No Show</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  name="notes"
                  label="Administrative Notes"
                >
                  <TextArea 
                    placeholder="Add any notes about this booking..."
                    rows={3}
                    style={{ minHeight: '48px' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Organization & Event Details */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '12px' 
          }}>
            <FontAwesomeIcon icon={faBuilding} style={{ color: '#722ed1', fontSize: '16px' }} />
            <Title level={4} style={{ margin: 0, color: '#1a1a1a' }}>
              Organization & Event Details
            </Title>
          </div>
          <div style={{ 
            marginBottom: '32px',
            padding: '24px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0'
          }}>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  name="businessEventName"
                  label="Business/Organization Name"
                >
                  <Input 
                    placeholder="Business or organization name" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="pointOfContactName"
                  label="Point of Contact"
                >
                  <Input 
                    placeholder="Contact person name" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  name="pointOfContactInfo"
                  label="Contact Information"
                >
                  <TextArea 
                    placeholder="Additional contact person information"
                    rows={2}
                    style={{ minHeight: '48px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item
                  name="eventStartTime"
                  label="Event Start Time"
                >
                  <Input 
                    placeholder="Event start time" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="screeningStartTime"
                  label="Screening Start Time"
                >
                  <Input 
                    placeholder="Screening start time" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="indoorOutdoorEvent"
                  label="Event Location Type"
                >
                  <Select 
                    placeholder="Select event type"
                    size="large"
                    style={{ height: '48px' }}
                  >
                    <Option value="indoor">Indoor Event</Option>
                    <Option value="outdoor">Outdoor Event</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  name="anticipatedParticipants"
                  label="Anticipated Participants"
                >
                  <Input 
                    placeholder="Expected number of participants" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="volunteersAvailable"
                  label="Volunteers Available"
                >
                  <Select 
                    placeholder="Are volunteers available?"
                    size="large"
                    style={{ height: '48px' }}
                  >
                    <Option value="yes">Yes</Option>
                    <Option value="no">No</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Address Information */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '12px' 
          }}>
            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#fa8c16', fontSize: '16px' }} />
            <Title level={4} style={{ margin: 0, color: '#1a1a1a' }}>
              Event Address
            </Title>
          </div>
          <div style={{ 
            marginBottom: '32px',
            padding: '24px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0'
          }}>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  name="address1"
                  label="Street Address"
                >
                  <Input 
                    placeholder="Street address" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  name="city"
                  label="City"
                >
                  <Input 
                    placeholder="City" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="state"
                  label="State"
                >
                  <Input 
                    placeholder="State" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="zipCode"
                  label="ZIP Code"
                >
                  <Input 
                    placeholder="ZIP Code" 
                    size="large"
                    style={{ height: '48px' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Footer Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '12px',
            paddingTop: '24px',
            borderTop: '2px solid #f0f0f0',
            marginTop: '24px'
          }}>
            <Button 
              onClick={handleCancel}
              icon={<FontAwesomeIcon icon={faTimes} />}
              size="large"
              style={{ 
                height: '48px',
                padding: '0 24px',
                fontSize: '16px'
              }}
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<FontAwesomeIcon icon={faSave} />}
              size="large"
              style={{ 
                height: '48px',
                padding: '0 24px',
                fontSize: '16px'
              }}
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default EditBookingModal;